import { useMemo, useRef, useState } from "react";
import {
  AUTHORABLE_TEMPLATE_CATEGORIES,
  OPT_OUT_FOOTER,
  TEMPLATE_CATEGORY_PRICING,
  formatTemplateRate,
  mentionsOptOut,
  createTemplateSchema,
  extractVariables,
  parameterFormat,
  templateCategoryLabel,
  templateCategorySchema,
  updateTemplateSchema,
  type CreateTemplateInput,
  type TemplateCategory,
  type TemplateMediaKind,
  type TemplateParameterFormat,
  type UpdateTemplateInput,
  type WabaTemplate,
  type WabaTemplateMedia,
} from "@school-sales-ai/shared";
import { HeaderMediaPicker } from "./HeaderMediaPicker";
import { TemplatePreview } from "./TemplatePreview";
import { VariableExamplesTable } from "./VariableExamplesTable";
import { VariableToolbar } from "./VariableToolbar";

/**
 * What the form offers for the header.
 *
 * `NONE` is a UI-only value: on the wire "no header" is `headerFormat: "TEXT"`
 * with a null text, and keeping the wire enum at three values is what let every
 * existing caller and fixture keep validating. The mapping lives here, in the
 * one place that renders the choice.
 */
const HEADER_CHOICES = ["NONE", "TEXT", "IMAGE", "DOCUMENT"] as const;
type HeaderChoice = (typeof HEADER_CHOICES)[number];

const HEADER_CHOICE_LABEL: Record<HeaderChoice, string> = {
  NONE: "Sin encabezado",
  TEXT: "Texto",
  IMAGE: "Imagen",
  DOCUMENT: "Documento (PDF)",
};

type Draft = {
  name: string;
  language: string;
  category: TemplateCategory;
  headerChoice: HeaderChoice;
  header: string;
  headerMedia: WabaTemplateMedia | null;
  body: string;
  footer: string;
  bodyExamples: string[];
};

const EMPTY: Draft = {
  name: "",
  language: "es_MX",
  // The CONSERVATIVE default, not a claim about what schools write. Defaulting to
  // utility would quote MXN 0.1565 for a message Meta will bill at 0.5614 — the
  // exact under-quote the `category`-not-`requestedCategory` rule exists to stop.
  // Most of what a school writes here is marketing anyway (reengagement,
  // invitations, admissions news), so the safe default is also the common one.
  category: "MARKETING",
  headerChoice: "NONE",
  header: "",
  headerMedia: null,
  body: "",
  footer: "",
  bodyExamples: [],
};

function toDraft(template: WabaTemplate | null): Draft {
  if (!template) return EMPTY;
  // `category` is Meta's verdict, mirrored as VarChar over a vocabulary Meta keeps
  // growing — parse it rather than trust it. Editing preserves it: a text change
  // must never turn an approved UTILITY template into marketing (~3.5x the cost).
  // Not `requestedCategory`: re-sending a category Meta already overruled asks for
  // a reclassification that has already happened.
  const parsed = templateCategorySchema.safeParse(template.category);
  return {
    name: template.name,
    language: template.language,
    category: parsed.success ? parsed.data : "MARKETING",
    headerChoice: headerChoiceOf(template),
    header: template.header ?? "",
    headerMedia: template.headerMedia,
    body: template.body,
    footer: template.footer ?? "",
    bodyExamples: template.bodyExamples,
  };
}

/**
 * Which choice an existing template shows.
 *
 * `headerFormat` is a plain string on the DTO because Meta's vocabulary grows
 * (VIDEO, LOCATION), so an unknown value falls back to NONE rather than
 * rendering a blank select — and the format lock below means such a template is
 * read-only here anyway.
 */
function headerChoiceOf(template: WabaTemplate): HeaderChoice {
  if (template.headerFormat === "IMAGE") return "IMAGE";
  if (template.headerFormat === "DOCUMENT") return "DOCUMENT";
  return template.header ? "TEXT" : "NONE";
}

function toPayload(draft: Draft): Record<string, unknown> {
  const isMedia = draft.headerChoice === "IMAGE" || draft.headerChoice === "DOCUMENT";
  return {
    name: draft.name,
    language: draft.language,
    category: draft.category,
    // NONE and TEXT are both `TEXT` on the wire; what separates them is whether
    // there is any text. Meta's header is text OR media, never both, so a media
    // header sends a null text even if the field still holds what was typed
    // before the admin switched — the schema rejects the combination.
    headerFormat: isMedia ? draft.headerChoice : "TEXT",
    header: isMedia || draft.header.trim() === "" ? null : draft.header,
    headerMediaAssetId: isMedia ? (draft.headerMedia?.assetId ?? null) : null,
    body: draft.body,
    footer: draft.footer.trim() === "" ? null : draft.footer,
    bodyExamples: draft.bodyExamples,
  };
}

/**
 * The parameter format the editor works in.
 *
 * Meta fixes it when the template is created, so an existing one keeps whatever
 * it already uses — the same preservation rule as the category. Everything new
 * is named, because `{{1}}` means nothing to the person writing the message.
 */
function formatFor(template: WabaTemplate | null): TemplateParameterFormat {
  return template ? parameterFormat(extractVariables(template.body)) : "NAMED";
}

/** The per-message rate for a category, formatted. Never a price typed into JSX. */
function rateOf(category: TemplateCategory): string {
  return formatTemplateRate(
    TEMPLATE_CATEGORY_PRICING.rates[category],
    TEMPLATE_CATEGORY_PRICING.currency,
  );
}

/**
 * The template editor.
 *
 * Validated with the SAME schema the endpoint uses, so the two cannot drift:
 * `safeParse` in a `useMemo`, issues mapped by `path.join(".")`, errors hidden
 * until the first save attempt.
 */
export function TemplateEditorDialog({
  template,
  onClose,
  onCreate,
  onUpdate,
  saving,
  onUploadMedia,
  uploadingMedia,
}: {
  template: WabaTemplate | null;
  onClose: () => void;
  onCreate: (input: CreateTemplateInput) => void;
  onUpdate: (input: UpdateTemplateInput) => void;
  saving: boolean;
  /**
   * Uploads the header file and hands back the stored asset.
   *
   * A prop rather than a mutation inside, matching `LogoSettings`' shape and for
   * the same reason: a multipart upload cannot ride the form's save cycle, so it
   * commits immediately — and keeping it out of here leaves this component pure
   * and testable without a query provider.
   */
  onUploadMedia: (
    file: File,
    kind: TemplateMediaKind,
    onDone: (media: WabaTemplateMedia) => void,
  ) => void;
  uploadingMedia: boolean;
}): React.JSX.Element {
  const isEdit = template !== null;
  const [draft, setDraft] = useState<Draft>(() => toDraft(template));
  /**
   * Whether the category is still the admin's to choose.
   *
   * Meta fixes a template's name, language and category when it approves it, and
   * there is no API to change them — so on a row that exists in Meta the picker
   * would offer something Meta rejects, and a text edit could otherwise turn an
   * approved UTILITY template into marketing (~3.5x the cost).
   *
   * A REJECTED row is the exception, and it matters: changing the category and
   * resubmitting is the only fix Meta offers for an `INCORRECT_CATEGORY`
   * rejection. Nothing is at risk there — the row has no approved price to
   * protect. A category outside the picker (AUTHENTICATION, or a value from
   * Meta's growing vocabulary) also stays locked: a `<select>` whose value
   * matches no option renders blank and the first change would clobber it.
   */
  const categoryLocked =
    (template !== null &&
      template.metaTemplateId !== null &&
      template.status !== "REJECTED") ||
    !AUTHORABLE_TEMPLATE_CATEGORIES.includes(draft.category);
  /**
   * Whether the header's TYPE is still the admin's to choose.
   *
   * Meta fixes the header format at approval, exactly like name, language and
   * category. Unlike category there is **no rejected-template exception**:
   * re-categorizing and resubmitting is Meta's own fix for INCORRECT_CATEGORY,
   * but there is no API to turn a TEXT header into an IMAGE one on any existing
   * template, rejected or not. The fix is a new template, and the copy says so.
   *
   * Backed by a server-side guard in `waba-template.service.update` — a control
   * this component hides is not a validation.
   */
  const headerFormatLocked = template !== null && template.metaTemplateId !== null;
  const [errorsRevealed, setErrorsRevealed] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const [format] = useState<TemplateParameterFormat>(() => formatFor(template));
  // Parsed once here and handed down, rather than re-derived by every component
  // that needs it.
  const variables = useMemo(() => extractVariables(draft.body), [draft.body]);

  const errors = useMemo(() => {
    const schema = isEdit ? updateTemplateSchema : createTemplateSchema;
    const result = schema.safeParse(toPayload(draft));
    const map = new Map<string, string>();
    if (!result.success) {
      for (const issue of result.error.issues) {
        const key = issue.path.join(".");
        // Every example error is the same fix; showing the first is enough.
        const collapsed = key.startsWith("bodyExamples") ? "bodyExamples" : key;
        if (!map.has(collapsed)) map.set(collapsed, issue.message);
      }
    }
    return map;
  }, [draft, isEdit]);

  const errorFor = (key: string): string | undefined =>
    errorsRevealed ? errors.get(key) : undefined;

  function submit(): void {
    setErrorsRevealed(true);
    if (errors.size > 0) return;
    const payload = toPayload(draft);
    if (isEdit) {
      onUpdate(updateTemplateSchema.parse(payload));
    } else {
      onCreate(createTemplateSchema.parse(payload));
    }
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void =>
    setDraft((d) => ({ ...d, [key]: value }));

  /**
   * Drops `{{token}}` where the caret is, and leaves the caret after it.
   *
   * Appending at the end instead would make the button useless for the common
   * case — the admin writes the sentence, then marks the part that changes.
   */
  function insertVariable(token: string): void {
    const textarea = bodyRef.current;
    const snippet = `{{${token}}}`;
    const start = textarea?.selectionStart ?? draft.body.length;
    const end = textarea?.selectionEnd ?? draft.body.length;
    const next = draft.body.slice(0, start) + snippet + draft.body.slice(end);
    set("body", next);

    const caret = start + snippet.length;
    // After React has re-rendered with the new value; setting it now would be
    // overwritten by the controlled update.
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(caret, caret);
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4">
      <div className="max-h-full w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-ink-900">
          {isEdit ? "Editar plantilla" : "Nueva plantilla"}
        </h2>

        {isEdit && template.metaTemplateId ? (
          <p className="mt-1 text-xs text-ink-500">
            Al guardar, los cambios se envían a Meta para revisión. El nombre y el
            idioma identifican la plantilla y no se pueden cambiar.
          </p>
        ) : null}

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-ink-800" htmlFor="tpl-name">
                Nombre
              </label>
              <input
                id="tpl-name"
                type="text"
                value={draft.name}
                disabled={isEdit}
                onChange={(e) => set("name", e.target.value.toLowerCase())}
                placeholder="recordatorio_visita"
                className="mt-1 w-full rounded-md border border-ink-200 px-3 py-1.5 text-sm disabled:bg-ink-050 disabled:text-ink-500"
              />
              <p className="mt-1 text-xs text-ink-500">
                Sólo minúsculas, números y guiones bajos.
              </p>
              {errorFor("name") ? (
                <p className="text-xs text-red-600">{errorFor("name")}</p>
              ) : null}
            </div>

            {/* The picker is a REQUEST, not a decision: Meta owns the final
                category and recategorizes on its own, so the rate is stated next
                to the choice rather than promised by it. See `categoryLocked`
                for why an approved template only reads it. */}
            {categoryLocked ? (
              <div className="rounded-md border border-ink-200 bg-ink-050 p-3 text-xs text-ink-600">
                Es una plantilla de{" "}
                <span className="font-medium text-ink-800">
                  {templateCategoryLabel(draft.category)}
                </span>
                : cuesta {rateOf(draft.category)} por mensaje entregado en{" "}
                {TEMPLATE_CATEGORY_PRICING.country} ({TEMPLATE_CATEGORY_PRICING.asOf}
                ). Meta fija la categoría al aprobar la plantilla, así que ya no se
                puede cambiar aquí.
              </div>
            ) : (
              <div>
                <label
                  className="text-sm font-medium text-ink-800"
                  htmlFor="tpl-category"
                >
                  Categoría
                </label>
                <select
                  id="tpl-category"
                  value={draft.category}
                  onChange={(e) => {
                    // Narrowed with the schema rather than cast — §6.10 forbids
                    // `as`, and the parse is already imported.
                    const parsed = templateCategorySchema.safeParse(e.target.value);
                    if (parsed.success) set("category", parsed.data);
                  }}
                  className="mt-1 w-full rounded-md border border-ink-200 px-3 py-1.5 text-sm"
                >
                  {AUTHORABLE_TEMPLATE_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {templateCategoryLabel(category)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-ink-500">
                  Cuesta {rateOf(draft.category)} por mensaje entregado en{" "}
                  {TEMPLATE_CATEGORY_PRICING.country} (
                  {TEMPLATE_CATEGORY_PRICING.asOf}). Meta puede reclasificarla según
                  su contenido; la categoría que quede es la que se cobra.
                </p>
                {/* The two conditions are Meta's own, and the volume sentence is
                    there because it is the question everyone asks: Meta
                    categorizes on content and intent, never on how many people
                    the message goes to. */}
                {draft.category === "UTILITY" ? (
                  <p className="mt-1.5 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Utilidad es sólo un mensaje <strong>sin intención promocional</strong>{" "}
                    y <strong>sobre algo que ese prospecto ya hizo</strong> — su
                    solicitud, su cita, su trámite. Una invitación, una promoción o un
                    «te extrañamos» es marketing, aunque lo envíes a poca gente: Meta
                    no mira a cuántos va, sino qué dice. Si la reclasifica —avisa con
                    un día y la plantilla sigue funcionando— pasa a costar{" "}
                    {rateOf("MARKETING")} por mensaje en vez de {rateOf("UTILITY")}.
                    Tienes 60 días para apelar en el Business Support Home de Meta.
                  </p>
                ) : null}
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-ink-800" htmlFor="tpl-header-type">
                Encabezado <span className="text-ink-500">(opcional: texto, imagen o PDF)</span>
              </label>
              {headerFormatLocked ? (
                // Rendered as a sentence and not as a disabled <select>: a
                // template reconciled in from Meta's dashboard can carry a format
                // outside our list (VIDEO, LOCATION), and a select whose value
                // matches no option renders blank.
                <p className="mt-1 text-xs text-ink-600">
                  Tipo:{" "}
                  <span className="font-medium text-ink-800">
                    {HEADER_CHOICE_LABEL[draft.headerChoice]}
                  </span>
                  . Meta lo fija al crear la plantilla y no permite cambiarlo; para otro
                  tipo de encabezado, crea una plantilla nueva.
                </p>
              ) : (
                <select
                  id="tpl-header-type"
                  value={draft.headerChoice}
                  onChange={(e) => {
                    const next = e.target.value as HeaderChoice;
                    setDraft((d) => ({
                      ...d,
                      headerChoice: next,
                      // The previously picked file is dropped when the KIND
                      // changes, because an image is not a valid document and
                      // the server rejects the mismatch. Kept when it does not.
                      headerMedia: next === d.headerChoice ? d.headerMedia : null,
                    }));
                  }}
                  className="mt-1 w-full rounded-md border border-ink-200 px-3 py-1.5 text-sm"
                >
                  {HEADER_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {HEADER_CHOICE_LABEL[choice]}
                    </option>
                  ))}
                </select>
              )}

              {draft.headerChoice === "TEXT" ? (
                <>
                  <input
                    id="tpl-header"
                    type="text"
                    aria-label="Texto del encabezado"
                    value={draft.header}
                    maxLength={60}
                    onChange={(e) => set("header", e.target.value)}
                    className="mt-2 w-full rounded-md border border-ink-200 px-3 py-1.5 text-sm"
                  />
                  {errorFor("header") ? (
                    <p className="text-xs text-red-600">{errorFor("header")}</p>
                  ) : null}
                </>
              ) : null}

              {draft.headerChoice === "IMAGE" || draft.headerChoice === "DOCUMENT" ? (
                <>
                  <HeaderMediaPicker
                    kind={draft.headerChoice === "IMAGE" ? "image" : "document"}
                    media={draft.headerMedia}
                    isUploading={uploadingMedia}
                    error={errorFor("headerMediaAssetId")}
                    onPick={(file) => {
                      onUploadMedia(
                        file,
                        draft.headerChoice === "IMAGE" ? "image" : "document",
                        (media) => set("headerMedia", media),
                      );
                    }}
                  />
                  <p className="mt-1.5 text-xs text-ink-500">
                    {isEdit
                      ? "Este archivo es la muestra que Meta revisa. En cada envío puedes usar otro."
                      : "Meta revisa este archivo para aprobar la plantilla. Será también el que se proponga por omisión en cada envío."}
                  </p>
                </>
              ) : null}
            </div>

            <div>
              <label className="text-sm font-medium text-ink-800" htmlFor="tpl-body">
                Cuerpo del mensaje
              </label>
              <textarea
                id="tpl-body"
                ref={bodyRef}
                value={draft.body}
                rows={5}
                maxLength={1024}
                onChange={(e) => set("body", e.target.value)}
                placeholder="Hola, te esperamos el próximo martes."
                className="mt-1 w-full rounded-md border border-ink-200 px-3 py-1.5 text-sm"
              />
              <p className="mt-1 text-xs text-ink-500">{draft.body.length}/1024</p>
              <VariableToolbar
                format={format}
                body={draft.body}
                onInsert={insertVariable}
              />
              {errorFor("body") ? (
                <p className="text-xs text-red-600">{errorFor("body")}</p>
              ) : null}
            </div>

            <div>
              <label className="text-sm font-medium text-ink-800" htmlFor="tpl-footer">
                Pie <span className="text-ink-500">(opcional)</span>
              </label>
              <input
                id="tpl-footer"
                type="text"
                value={draft.footer}
                maxLength={60}
                onChange={(e) => set("footer", e.target.value)}
                className="mt-1 w-full rounded-md border border-ink-200 px-3 py-1.5 text-sm"
              />
              {errorFor("footer") ? (
                <p className="text-xs text-red-600">{errorFor("footer")}</p>
              ) : null}
              {/* Meta fixes a template's text at approval, so nothing can be
                  appended at send time — authoring is the ONLY point of leverage
                  for an opt-out, which is why this is one click here rather than
                  a rule enforced later. The word it inserts is the same constant
                  the inbound detector listens for, so the message cannot promise
                  something the system ignores. */}
              {draft.category === "MARKETING" &&
              !mentionsOptOut(draft.body, draft.footer) ? (
                <button
                  type="button"
                  onClick={() => {
                    set("footer", OPT_OUT_FOOTER);
                  }}
                  className="mt-1.5 text-xs text-moss underline"
                >
                  Agregar pie de baja («{OPT_OUT_FOOTER}»)
                </button>
              ) : null}
            </div>
          </div>

          <div className="space-y-4">
            <VariableExamplesTable
              variables={variables}
              format={format}
              examples={draft.bodyExamples}
              onChange={(next) => set("bodyExamples", next)}
              error={errorFor("bodyExamples")}
            />
            <div>
              <p className="mb-2 text-sm font-medium text-ink-800">Vista previa</p>
              <TemplatePreview
                header={draft.headerChoice === "TEXT" && draft.header !== "" ? draft.header : null}
                headerMedia={
                  draft.headerChoice === "IMAGE" || draft.headerChoice === "DOCUMENT"
                    ? draft.headerMedia
                    : null
                }
                body={draft.body}
                footer={draft.footer === "" ? null : draft.footer}
                examples={draft.bodyExamples}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ink-200 px-4 py-2 text-sm text-ink-700"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="rounded-md bg-ink-900 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
